import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { SystemMessageContext } from '../../context/SystemMessageContext';
import callAPI from '../../helpers/callAPI';
import useCallApi from '../../hooks/useCallApi';
import CampaignList from '../../pages/Advertiser/CampaignList/CampaignList';
import ScheduleRelaunchModal from '../../pages/Campaign/ScheduleRelaunchModal/ScheduleRelaunchModal';
import Advertiser from '../../templates/Advertiser/Advertiser';
import Campaign from '../../templates/Campaign/Campaign';
import Loading from '../../templates/UI/Loading/Loading';
import NotesLog from '../NotesLog/NotesLog';
import DocuSignInsertionOrderContent from './DocusignInsertionOrderContent';
import { useAuth, useProtectedContent } from '../../context/AuthContext';

export default function CampaignSetupContent({ campaignPhaseData, closeModal, refetch }) {
    const auth = useAuth();
    const showDocusignIO = useProtectedContent(auth.permissions, 'docusign_io')
    const { showSystemMessage } = useContext(SystemMessageContext)
    const [phase, setPhase] = useState(parseInt(campaignPhaseData.phase_id) || 10)
    const [checkAll, setCheckAll] = useState(false)
    const [verified, setVerified] = useState(false)
    const { data: phaseData, dataLoaded: phaseDataLoaded } = useCallApi('campaigns/phases', 'GET', {}, [], showSystemMessage)
    const { data: phaseQuestionsData, setData: setPhaseQuestionsData, dataLoaded: phaseQuestionsDataLoaded, refetch: phaseQuestionsRefetch } = useCallApi(`campaigns/phaseQuestions?id=${phase}&offerId=${campaignPhaseData.offer_id}`, 'GET', {}, [phase], showSystemMessage)
    const [refreshNotesLog, setRefreshNotesLog] = useState(false)
    const [showScheduleRelaunchModal, setShowScheduleRelaunchModal] = useState(false)
    const [showDocusignModal, setShowDocusignModal] = useState(false)

    useEffect(() => {
        //toggle checkall or uncheck all based on answers
        if (phaseQuestionsDataLoaded && phaseQuestionsData.success) {
            const allChecked = phaseQuestionsData.questions.every((question) => question.answer === 'Y')
            if (!allChecked) {
                setCheckAll(true)
            } else {
                setCheckAll(false)
            }
        }
    }, [phaseQuestionsDataLoaded, phaseQuestionsData])

    const handlePhaseChange = async (phaseId) => {
        setPhase(parseInt(phaseId))
        const response = await callAPI(`campaigns/phase?phaseId=${phaseId}&id=${campaignPhaseData.offer_id}`, 'PUT', {}, [], showSystemMessage)

        // Get phase name from phaseData
        const phaseName = phaseData.data.find(phase => phase.phase_id == phaseId)?.phase_name || 'Unknown Phase'

        // Add note to campaign logs for any phase change
        const formData = new FormData()
        formData.append('log', `Campaign phase changed to: ${phaseName}`)
        await callAPI(`offerSetup/${campaignPhaseData.offer_id}/log`, 'POST', formData, true)
        setRefreshNotesLog(!refreshNotesLog)
    }

    const toggleAllAnswers = (answer) => {

        const questionIds = phaseQuestionsData.questions.map((question) => question.question_id)
        const questionAnswers = questionIds.map(() => answer)

        callAPI(`campaigns/campaignQuestions?id=${campaignPhaseData.offer_id}&phaseId=${campaignPhaseData.phase_id}`, 'POST', { questions: questionIds, answers: questionAnswers })

        const updatedQuestions = phaseQuestionsData.questions.map((question) => {
            return { ...question, answer }
        })
        setPhaseQuestionsData({ ...phaseQuestionsData, questions: updatedQuestions })
    }

    const handleCheck = async (questionId) => {
        const currentAnswer = phaseQuestionsData.questions.find((question) => question.question_id === questionId).answer

        await callAPI(`campaigns/campaignQuestions?id=${campaignPhaseData.offer_id}&phaseId=${campaignPhaseData.phase_id}`, 'POST', { questions: [questionId], answers: [currentAnswer === 'Y' ? 'N' : 'Y'] })

        const updatedQuestions = phaseQuestionsData.questions.map((question) => {
            if (question.question_id === questionId) {
                return { ...question, answer: currentAnswer === 'Y' ? 'N' : 'Y' }
            }
            return question
        })
        setPhaseQuestionsData({ ...phaseQuestionsData, questions: updatedQuestions })

    }

    const hasButton = (text, showDocusignIO) => {
        if (text.includes('Sign and store')) {
            // Split text by "Sign and store" phrase to get the parts before and after
            const parts = text.split('Sign and store');
            return (
                <span>
                    {parts[0]}
                    {
                        showDocusignIO && 
                            <Button 
                                variant="link" 
                                size="sm" 
                                onClick={() => setShowDocusignModal(true)}
                                className="p-0 m-0"
                                style={{ verticalAlign: 'baseline', fontWeight: 'normal' }}
                            >
                                Sign and store
                            </Button>
                    }
                    
                    {parts[1]}
                </span>
            )
        } else {
            return <span>{text}</span>
        }
    }

    return (
        <div className="card">
            {showScheduleRelaunchModal && <ScheduleRelaunchModal
                isOpen={showScheduleRelaunchModal}
                handleClose={() => setShowScheduleRelaunchModal(false)}
                campaignId={campaignPhaseData.offer_id}
                refetch={async (date, status) => {
                    //add log
                    const formData = new FormData()
                    formData.append('log', `Campaign relaunch to [${status}] scheduled for: ${moment(date).format('YYYY-MM-DD HH:mm')}`)
                    await callAPI(`offerSetup/${campaignPhaseData.offer_id}/log`, 'POST', formData, true)
                    setRefreshNotesLog(!refreshNotesLog)
                    //update phase to 40: Launched
                    await callAPI(`campaigns/phase?phaseId=40&id=${campaignPhaseData.offer_id}`, 'PUT', {}, [], showSystemMessage)
                    //close modal
                    setShowScheduleRelaunchModal(false)

                    // Call refetch to update parent component and ensure UI is updated
                    if (refetch) refetch()
                    closeModal()
                }}
            />}
            <div className="card-header">Campaign Setup</div>
            <div className="card-body p-0">
                <div className="alert alert-info m-3">
                    Page Link: <Link to={`/advertiser/${campaignPhaseData.merchant_id}/campaignSetup?id=${campaignPhaseData.offer_id}`}>{`${process.env.REACT_APP_PORTAL_URL}/advertiser/${campaignPhaseData.merchant_id}/campaignSetup?id=${campaignPhaseData.offer_id}`}</Link>
                    <span className='ml-3'>
                        <CopyToClipboard text={`${process.env.REACT_APP_PORTAL_URL}/advertiser/${campaignPhaseData.merchant_id}/campaignSetup?id=${campaignPhaseData.offer_id}`}>
                            <button type="button" className="btn btn-outline-primary shadow-none" size="sm">
                                <FontAwesomeIcon icon="copy" />
                            </button>
                        </CopyToClipboard>
                    </span>
                </div>
                <div className="alert border d-flex justify-content-between m-3">
                    <div className="d-flex align-items-center">
                        <Campaign oid={campaignPhaseData.offer_id} name={campaignPhaseData.offer_name} />
                        <div className="ml-3">
                            ({campaignPhaseData.status})
                        </div>
                    </div>
                    <div className="d-flex align-items-center">
                        <Advertiser className='mr-1' id={campaignPhaseData.merchant_id} name={campaignPhaseData.merchant_name} /> | Manager: {campaignPhaseData.full_name} | Bizdev: {campaignPhaseData.bd_manager} {campaignPhaseData.contract_url !== '' && <> | <a className='mx-1' href={campaignPhaseData.contract_url} target="_blank" rel="noreferrer">Contract </a></>} | Platform: {campaignPhaseData.platform_name}
                    </div>
                </div>
                {true ? (
                    <div className="m-3">
                        <div className="row">
                            <div className="col-xl-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <NotesLog uri={`offerSetup`} id={campaignPhaseData.offer_id} refresh={refreshNotesLog} postAsFollowupOption fixedHeight={false} advertiserId={campaignPhaseData.merchant_id} />
                            </div>
                            <div className="col-xl-6">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <div>Phase</div>
                                            <div>
                                                <select className="form-control form-control-sm ml-3"
                                                    onChange={(e) => handlePhaseChange(e.target.value)}
                                                    value={phase}>
                                                    {phase === 50 && (
                                                        <option value={50}>Complete</option>
                                                    )}
                                                    {phaseDataLoaded && phaseData.data.map((phase, index) => {
                                                        if (phase.phase_id === 50) {
                                                            return null
                                                        } else {
                                                            return <option key={index} value={phase.phase_id}>{phase.phase_name}</option>
                                                        }
                                                    }
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            {checkAll ? (
                                                <button size='sm' className="btn btn-primary" onClick={() => toggleAllAnswers('Y')} >Check All</button>
                                            ) : (
                                                <button size='sm' className="btn btn-primary" onClick={() => toggleAllAnswers('N')} >Uncheck All</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-body p-0 d-flex ">
                                            <Modal show={showDocusignModal} onHide={() => setShowDocusignModal(false)} size="lg">
                                                <Modal.Header closeButton>
                                                    <Modal.Title>Send Insertion Order Contract</Modal.Title>
                                                </Modal.Header>
                                                <Modal.Body>
                                                    {/* <DocuSignContent templateId = 'dfda10b0-1b91-4522-9a15-88e9fe4ff7cf' /> */}
                                                    <DocuSignInsertionOrderContent offerId={campaignPhaseData.offer_id} closeModal={() => setShowDocusignModal(false)} />
                                                </Modal.Body>
                                            </Modal>
                                            
                                        {phaseQuestionsDataLoaded && phaseQuestionsData.success ? (
                                            <table className="table table-striped table-card m-0 table-hover">
                                                <tbody>
                                                    {phaseQuestionsData.questions.map((question, index) => (
                                                        <tr key={index}>
                                                            <td><input id={index} type="checkbox" checked={question.answer === "Y"} onClick={() => handleCheck(question.question_id)} /></td>
                                                            <td><label className="p-0 m-0" htmlFor={index}>{hasButton(question.question_text, showDocusignIO)}</label></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <Loading />
                                        )}
                                    </div>
                                    {phase == 40 && (
                                        <div className="card-footer">
                                            <Alert variant="info" className='m-0  d-flex justify-content-between align-items-center'>
                                                <div className='m-0  d-flex justify-content-between align-items-center'>
                                                    <input id="confirm_complete" type="checkbox" className="mr-2" value={verified} onChange={(e) => setVerified(e.target.checked)} />
                                                    <label className="m-0 p-0" htmlFor="confirm_complete">I confirm that the above has been verified.</label>
                                                </div>
                                                <button size='sm' className="btn btn-primary" disabled={!verified} onClick={() => handlePhaseChange(50)} >Complete</button>
                                            </Alert>

                                        </div>
                                    )}
                                </div>
                                {
                                    (phase === 30 || phase === 35) &&
                                    <div className='mt-3'>
                                        <button className="btn btn-warning" onClick={(e) => { e.preventDefault(); setShowScheduleRelaunchModal(true) }}> Schedule Relaunch </button>
                                    </div>
                                }
                                <div className='mt-3'>
                                    <CampaignList advertiserId={campaignPhaseData.merchant_id} />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Loading />
                )}
            </div>
        </div>
    )
}